import { Box, Stack, Text } from '@mantine/core';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { getDateSectionHeader, groupEventsByDate, Order } from 'src/meetings/utils/date-utils';
import type { FamilyMeeting } from 'src/meetings/hooks/useScopedMeetings';
import { MeetingCard } from './MeetingCard';

type Props = {
  meetings: FamilyMeeting[];
  order: Order;
  emptyMessage: string;
  showClientLabel?: boolean;
  onOpenParticipants?: (eventId: string) => void;
  onOpenDetails?: (eventId: string) => void;
  onGenerateMeetingNote?: (
    eventId: string,
    unidentifiedCount: number,
    firstSpeakerLabel?: string,
  ) => void;
  onAssignSpeaker?: (eventId: string, speakerLabel: string) => void;
};

export function FamilyMeetingsList({
  meetings,
  order,
  emptyMessage,
  showClientLabel = false,
  onOpenParticipants,
  onOpenDetails,
  onGenerateMeetingNote,
  onAssignSpeaker,
}: Props) {
  if (meetings.length === 0) {
    return (
      <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.6" ta="center" mt="lg">
        {emptyMessage}
      </Text>
    );
  }

  const groups = [...groupEventsByDate(meetings, order).entries()];

  return (
    <Stack gap="lg">
      {groups.map(([dateKey, dateEvents]) => (
        <Box key={dateKey}>
          <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.6">
            {sectionHeader(new Date(dateKey))}
          </Text>
          <Stack gap="xs" mt="xxs">
            {(dateEvents as FamilyMeeting[]).map(meeting => (
              <MeetingCard
                key={meeting.id}
                meeting={meeting}
                showClientLabel={showClientLabel}
                onOpenParticipants={onOpenParticipants}
                onOpenDetails={onOpenDetails}
                onGenerateMeetingNote={onGenerateMeetingNote}
                onAssignSpeaker={onAssignSpeaker}
              />
            ))}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

function sectionHeader(date: Date): string {
  return isYesterday(date) ? 'Yesterday' : getDateSectionHeader(date);
}

function isYesterday(date: Date): boolean {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return date.toDateString() === yesterday.toDateString();
}
